import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Settings, Calendar as CalendarIcon, Bell, Save, Mail, Send, Eye, Clock, GraduationCap, Globe, Loader2, X, Sliders, Snowflake, Search } from 'lucide-react';
import { ClassLevelsSection } from '@/components/configuration/ClassLevelsSection';
import { ReliefPrioritySection } from '@/components/configuration/ReliefPrioritySection';

const SectionHeader = ({ icon: Icon, title, description }) => (
  <div className="mb-4 pb-3 border-b border-slate-200">
    <div className="flex items-center gap-2.5">
      {Icon && (
        <div className="p-1.5 rounded-md bg-slate-100">
          <Icon className="h-4 w-4 text-slate-700" />
        </div>
      )}
      <h2 className="text-xs font-bold uppercase tracking-[0.15em] text-slate-700">
        {title}
      </h2>
    </div>
    {description && (
      <p className="text-sm text-slate-500 mt-1.5 ml-8">{description}</p>
    )}
  </div>
);


const TIMEZONE_OPTIONS = [
  { value: 'Asia/Singapore', label: 'Singapore (UTC+8)' },
  { value: 'Asia/Kuala_Lumpur', label: 'Kuala Lumpur (UTC+8)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (UTC+8)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (UTC+8)' },
  { value: 'Asia/Taipei', label: 'Taipei (UTC+8)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (UTC+9)' },
  { value: 'Asia/Seoul', label: 'Seoul (UTC+9)' },
  { value: 'Asia/Jakarta', label: 'Jakarta (UTC+7)' },
  { value: 'Asia/Bangkok', label: 'Bangkok (UTC+7)' },
  { value: 'Asia/Kolkata', label: 'India (UTC+5:30)' },
  { value: 'Asia/Dubai', label: 'Dubai (UTC+4)' },
  { value: 'Australia/Sydney', label: 'Sydney (UTC+10/11)' },
  { value: 'Australia/Melbourne', label: 'Melbourne (UTC+10/11)' },
  { value: 'Pacific/Auckland', label: 'Auckland (UTC+12/13)' },
  { value: 'Europe/London', label: 'London (UTC+0/1)' },
  { value: 'Europe/Paris', label: 'Paris (UTC+1/2)' },
  { value: 'Europe/Berlin', label: 'Berlin (UTC+1/2)' },
  { value: 'America/New_York', label: 'New York (UTC-5/-4)' },
  { value: 'America/Chicago', label: 'Chicago (UTC-6/-5)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (UTC-8/-7)' },
  { value: 'UTC', label: 'UTC' },
];

const ConfigurationPage = () => {
  const { token } = useContext(AuthContext);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newDisabledDate, setNewDisabledDate] = useState('');

  // Briefing state
  const [sendingBriefing, setSendingBriefing] = useState(false);
  const [briefingResult, setBriefingResult] = useState(null);
  const [briefingDate, setBriefingDate] = useState(new Date().toISOString().split('T')[0]);
  const [previewTeachers, setPreviewTeachers] = useState([]);
  const [selectedPreviewTeacher, setSelectedPreviewTeacher] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [briefingScheduleInfo, setBriefingScheduleInfo] = useState(null);

  // Class Absence state
  const [classAbsences, setClassAbsences] = useState([]);
  const [classLevels, setClassLevels] = useState([]);
  const [classAbsenceForm, setClassAbsenceForm] = useState({
    class_name: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    reason: ''
  });

  // Invigilation Business Rules state
  const [invigRules, setInvigRules] = useState(null);
  const [venues, setVenues] = useState([]);
  const [newVenue, setNewVenue] = useState({ name: '', capacity: 40, min_invigilators: 2 });
  const [newCustomRule, setNewCustomRule] = useState('');
  const [allTeachers, setAllTeachers] = useState([]);
  const [allJobTitles, setAllJobTitles] = useState([]);

  // Exam Timetable Freeze Periods
  const [freezePeriods, setFreezePeriods] = useState([]);
  const [freezeForm, setFreezeForm] = useState({
    scope: 'whole', // 'whole' | 'level' | 'class'
    level_id: '',
    classes: [], // array of class names
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    reason: ''
  });
  const [classNameOptions, setClassNameOptions] = useState([]);
  const [classInput, setClassInput] = useState('');
  const [showClassSuggestions, setShowClassSuggestions] = useState(false);

  useEffect(() => {
    fetchConfig();
    fetchClassAbsences();
    fetchClassLevels();
    fetchInvigRules();
    fetchVenues();
    fetchFreezePeriods();
    fetchBriefingScheduleInfo();
    // Also fetch teacher + job-title lists for exclusion dropdowns
    axios.get(`${API}/users/teachers-list`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAllTeachers(r.data)).catch(() => {});
    axios.get(`${API}/job-titles`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAllJobTitles(r.data)).catch(() => {});
    axios.get(`${API}/timetables/classes-list`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setClassNameOptions(r.data || [])).catch(() => {});
  }, []);

  const fetchBriefingScheduleInfo = async () => {
    try {
      const r = await axios.get(`${API}/briefing/schedule-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBriefingScheduleInfo(r.data);
    } catch (e) {
      // Endpoint may not exist on older deployments — silently ignore.
    }
  };

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(response.data);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Failed to load configuration:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const updates = {
        system_enabled: config.system_enabled,
        auto_disable_holidays: config.auto_disable_holidays,
        max_relief_per_teacher_per_week: config.max_relief_per_teacher_per_week,
        periods_per_day: config.periods_per_day,
        exclude_roles_from_relief: config.exclude_roles_from_relief || [],
        notification_channels: config.notification_channels,
        school_timezone: config.school_timezone || 'Asia/Singapore',
        audit_log_retention_days: config.audit_log_retention_days || 365,
        dpo_name: config.dpo_name || '',
        dpo_email: config.dpo_email || '',
        relief_max_reassignments: Number.isFinite(+config.relief_max_reassignments) ? +config.relief_max_reassignments : 3,
        invigilation_max_reassignments: Number.isFinite(+config.invigilation_max_reassignments) ? +config.invigilation_max_reassignments : 3,
        relief_priority_order: Array.isArray(config.relief_priority_order) && config.relief_priority_order.length > 0
          ? config.relief_priority_order
          : ["nominated", "same_subject", "same_level", "low_workload", "most_available"],
        relief_enforce_same_level: !!config.relief_enforce_same_level,
        // Daily briefing schedule — these were previously not being saved,
        // which is why "send at 7" wasn't taking effect.
        daily_briefing_enabled: config.daily_briefing_enabled !== false,
        daily_briefing_hour: Number.isFinite(+config.daily_briefing_hour) ? +config.daily_briefing_hour : 7,
        daily_briefing_minute: Number.isFinite(+config.daily_briefing_minute) ? +config.daily_briefing_minute : 0,
      };
      
      await axios.patch(`${API}/config`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuration saved successfully');
      fetchConfig();
      fetchBriefingScheduleInfo();
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleAddDisabledDate = async () => {
    if (!newDisabledDate) return;
    
    try {
      await axios.post(`${API}/config/disable-dates`, [newDisabledDate], {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Date added to disabled list');
      setNewDisabledDate('');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to add disabled date');
    }
  };

  const handleRemoveDisabledDate = async (date) => {
    try {
      await axios.delete(`${API}/config/disable-dates`, {
        headers: { Authorization: `Bearer ${token}` },
        data: [date]
      });
      toast.success('Date removed from disabled list');
      fetchConfig();
    } catch (error) {
      toast.error('Failed to remove disabled date');
    }
  };

  // Class Absence Declarations
  const fetchClassAbsences = async () => {
    try {
      const res = await axios.get(`${API}/class-absences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClassAbsences(res.data);
    } catch { /* silently fail */ }
  };

  const fetchClassLevels = async () => {
    try {
      const res = await axios.get(`${API}/class-levels`, { headers: { Authorization: `Bearer ${token}` } });
      setClassLevels(res.data);
    } catch { /* ignore */ }
  };

  const handleAddClassAbsence = async () => {
    if (!classAbsenceForm.class_name || !classAbsenceForm.start_date || !classAbsenceForm.end_date) {
      toast.error('Please fill in class name, start date, and end date');
      return;
    }
    if (classAbsenceForm.end_date < classAbsenceForm.start_date) {
      toast.error('End date must be on or after start date');
      return;
    }
    try {
      await axios.post(`${API}/class-absences`, classAbsenceForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Class absence declaration added');
      setClassAbsenceForm({ class_name: '', start_date: new Date().toISOString().split('T')[0], end_date: new Date().toISOString().split('T')[0], reason: '' });
      fetchClassAbsences();
    } catch (error) {
      toast.error('Failed to add class absence');
    }
  };

  const handleRemoveClassAbsence = async (id) => {
    try {
      await axios.delete(`${API}/class-absences/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Class absence declaration removed');
      fetchClassAbsences();
    } catch (error) {
      toast.error('Failed to remove class absence');
    }
  };

  // Invigilation Business Rules
  const fetchInvigRules = async () => {
    try {
      const res = await axios.get(`${API}/invigilation-rules`, { headers: { Authorization: `Bearer ${token}` } });
      setInvigRules(res.data);
    } catch { setInvigRules({}); }
  };

  const saveInvigRules = async () => {
    try {
      await axios.patch(`${API}/invigilation-rules`, invigRules, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Invigilation business rules saved');
    } catch { toast.error('Failed to save rules'); }
  };

  const fetchVenues = async () => {
    try {
      const res = await axios.get(`${API}/venues`, { headers: { Authorization: `Bearer ${token}` } });
      setVenues(res.data);
    } catch {}
  };

  const handleAddVenue = async () => {
    if (!newVenue.name) { toast.error('Enter venue name'); return; }
    try {
      await axios.post(`${API}/venues`, newVenue, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Venue '${newVenue.name}' added`);
      setNewVenue({ name: '', capacity: 40, min_invigilators: 2 });
      fetchVenues();
    } catch { toast.error('Failed to add venue'); }
  };

  const handleDeleteVenue = async (id) => {
    try {
      await axios.delete(`${API}/venues/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Venue removed');
      fetchVenues();
    } catch { toast.error('Failed to remove venue'); }
  };

  // Exam Timetable Freeze Periods
  const fetchFreezePeriods = async () => {
    try {
      const res = await axios.get(`${API}/exam-freeze-periods`, { headers: { Authorization: `Bearer ${token}` } });
      setFreezePeriods(res.data);
    } catch { /* ignore */ }
  };

  const handleAddFreezePeriod = async () => {
    if (!freezeForm.start_date || !freezeForm.end_date) {
      toast.error('Start and end date are required');
      return;
    }
    if (freezeForm.end_date < freezeForm.start_date) {
      toast.error('End date must be on or after start date');
      return;
    }
    try {
      if (freezeForm.scope === 'class') {
        const classList = Array.from(new Set((freezeForm.classes || []).map(c => (c || '').trim()).filter(Boolean)));
        if (classList.length === 0) {
          toast.error('Add at least one class');
          return;
        }
        // Create a class-absence record per class (reuses existing engine hook)
        await Promise.all(classList.map(cls =>
          axios.post(`${API}/class-absences`, {
            class_name: cls,
            start_date: freezeForm.start_date,
            end_date: freezeForm.end_date,
            reason: freezeForm.reason || 'Exam timetable freeze',
          }, { headers: { Authorization: `Bearer ${token}` } })
        ));
        toast.success(`Frozen ${classList.length} class${classList.length > 1 ? 'es' : ''} for the selected period`);
        fetchClassAbsences();
      } else if (freezeForm.scope === 'level') {
        if (!freezeForm.level_id) {
          toast.error('Select a level');
          return;
        }
        await axios.post(`${API}/exam-freeze-periods`, {
          start_date: freezeForm.start_date,
          end_date: freezeForm.end_date,
          scope: 'level',
          level_id: freezeForm.level_id,
          reason: freezeForm.reason || '',
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Level-scoped exam timetable freeze added');
        fetchFreezePeriods();
      } else {
        await axios.post(`${API}/exam-freeze-periods`, {
          start_date: freezeForm.start_date,
          end_date: freezeForm.end_date,
          scope: 'whole',
          reason: freezeForm.reason || '',
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Whole-school exam timetable freeze added');
        fetchFreezePeriods();
      }
      setFreezeForm({
        scope: freezeForm.scope,
        level_id: '',
        classes: [],
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        reason: ''
      });
      setClassInput('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add freeze period');
    }
  };

  const handleRemoveFreezePeriod = async (id) => {
    try {
      await axios.delete(`${API}/exam-freeze-periods/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Freeze period removed');
      fetchFreezePeriods();
    } catch {
      toast.error('Failed to remove freeze period');
    }
  };

  // Chip-input helpers for class autocomplete
  const addClassChip = (raw) => {
    const name = (raw || '').trim();
    if (!name) return;
    setFreezeForm(prev => prev.classes.includes(name) ? prev : { ...prev, classes: [...prev.classes, name] });
    setClassInput('');
    setShowClassSuggestions(false);
  };

  const removeClassChip = (name) => {
    setFreezeForm(prev => ({ ...prev, classes: prev.classes.filter(c => c !== name) }));
  };

  const classSuggestions = (() => {
    const q = classInput.trim().toLowerCase();
    const selected = new Set(freezeForm.classes);
    return classNameOptions
      .filter(c => !selected.has(c))
      .filter(c => !q || c.toLowerCase().includes(q))
      .slice(0, 8);
  })();

  const handleClassInputKey = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      if (classInput.trim()) {
        e.preventDefault();
        addClassChip(classInput);
      }
    } else if (e.key === 'Backspace' && !classInput && freezeForm.classes.length > 0) {
      // Remove last chip on backspace in empty input
      removeClassChip(freezeForm.classes[freezeForm.classes.length - 1]);
    }
  };

  const handleSendBriefing = async () => {
    setSendingBriefing(true);
    setBriefingResult(null);
    try {
      const res = await axios.post(`${API}/briefing/send-daily?date=${briefingDate}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBriefingResult(res.data);
      toast.success(res.data.message);
    } catch (error) {
      toast.error('Failed to send daily briefing');
    } finally {
      setSendingBriefing(false);
    }
  };

  const handlePreviewBriefing = async () => {
    if (!selectedPreviewTeacher) {
      toast.error('Please select a teacher to preview');
      return;
    }
    try {
      const res = await axios.get(`${API}/briefing/preview/${selectedPreviewTeacher}?date=${briefingDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewHtml(res.data);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load preview');
    }
  };

  const fetchPreviewTeachers = async () => {
    try {
      const res = await axios.get(`${API}/briefing/preview-list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreviewTeachers(res.data);
    } catch (error) {
      // silently fail
    }
  };

  useEffect(() => {
    fetchPreviewTeachers();
  }, []);

  if (loading) {
    return <div className="text-slate-600">Loading configuration...</div>;
  }

  if (!config) {
    return <div className="text-slate-600">Failed to load configuration</div>;
  }

  return (
    <div data-testid="configuration-page">
      <div className="mb-6">
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter text-slate-900">Configuration</h1>
        <p className="text-base text-slate-500 mt-2">Manage system settings, scheduling, communications, and exam rules</p>
      </div>

      {/* Quick-access section chips + keyword search — sticky below the page header */}
      <div
        className="sticky top-0 z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 mb-6 py-2.5 bg-white/85 backdrop-blur-md border-b border-slate-200"
        data-testid="config-section-nav"
      >
        <div className="flex flex-wrap items-center gap-2">
          {[
            { id: 'section-general', label: 'General & System', icon: Sliders },
            { id: 'section-notifications', label: 'Notifications', icon: Bell },
            { id: 'section-scheduling', label: 'Scheduling', icon: CalendarIcon },
            { id: 'section-levels', label: 'Class Levels', icon: Sliders },
            { id: 'section-exams', label: 'Exams', icon: GraduationCap },
          ].map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => {
                const el = document.querySelector(`[data-testid="${id}"]`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors"
              data-testid={`config-nav-${id}`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
          <ConfigSearch />
        </div>
      </div>

      <div className="space-y-10">
        {/* ─── Section: General & System ─── */}
        <section data-testid="section-general" id="section-general" className="scroll-mt-24">
          <SectionHeader icon={Sliders} title="General & System" description="Core toggles that govern the whole platform" />
          <div className="space-y-6">
        {/* System Status */}
        <Card className="dashboard-card p-6">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-md bg-blue-50">
              <Settings className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">System Status</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="system-enabled" className="text-base font-medium">System Enabled</Label>
                    <p className="text-sm text-slate-500">Enable or disable the entire relief system</p>
                  </div>
                  <Switch
                    id="system-enabled"
                    checked={config.system_enabled}
                    onCheckedChange={(checked) => setConfig({ ...config, system_enabled: checked })}
                    data-testid="system-enabled-toggle"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="auto-disable" className="text-base font-medium">Auto-Disable Holidays</Label>
                    <p className="text-sm text-slate-500">Automatically disable system on public holidays</p>
                  </div>
                  <Switch
                    id="auto-disable"
                    checked={config.auto_disable_holidays}
                    onCheckedChange={(checked) => setConfig({ ...config, auto_disable_holidays: checked })}
                    data-testid="auto-disable-toggle"
                  />
                </div>
                <div>
                  <Label htmlFor="max-relief" className="text-base font-medium">Max Relief per Teacher per Week</Label>
                  <p className="text-sm text-slate-500 mb-2">Maximum number of relief duties per teacher</p>
                  <Input
                    id="max-relief"
                    type="number"
                    value={config.max_relief_per_teacher_per_week ?? ''}
                    onChange={(e) => setConfig({ ...config, max_relief_per_teacher_per_week: e.target.value === '' ? '' : parseInt(e.target.value) })}
                    min="0"
                    className="max-w-xs"
                    data-testid="max-relief-input"
                  />
                </div>
                <div>
                  <Label htmlFor="relief-max-reassign" className="text-base font-medium">Relief Auto-Reassign Limit</Label>
                  <p className="text-sm text-slate-500 mb-2">
                    Max times a relief duty is auto-reassigned after a decline. After this, the admin is alerted to assign manually. Default: 3.
                  </p>
                  <Input
                    id="relief-max-reassign"
                    type="number"
                    value={config.relief_max_reassignments ?? 3}
                    onChange={(e) => setConfig({ ...config, relief_max_reassignments: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                    min="0"
                    max="10"
                    className="max-w-xs"
                    data-testid="relief-max-reassign-input"
                  />
                </div>
                <div>
                  <Label htmlFor="invig-max-reassign" className="text-base font-medium">Invigilation Auto-Reassign Limit</Label>
                  <p className="text-sm text-slate-500 mb-2">
                    Max times an invigilation duty is auto-reassigned after a decline. Default: 3.
                  </p>
                  <Input
                    id="invig-max-reassign"
                    type="number"
                    value={config.invigilation_max_reassignments ?? 3}
                    onChange={(e) => setConfig({ ...config, invigilation_max_reassignments: e.target.value === '' ? 0 : parseInt(e.target.value) })}
                    min="0"
                    max="10"
                    className="max-w-xs"
                    data-testid="invig-max-reassign-input"
                  />
                </div>
                <div>
                  <Label htmlFor="periods-per-day" className="text-base font-medium">Periods Per Day</Label>
                  <p className="text-sm text-slate-500 mb-2">Number of teaching periods in a school day (current default is 8, can be changed as required)</p>
                  <Input
                    id="periods-per-day"
                    type="number"
                    value={config.periods_per_day ?? ''}
                    onChange={(e) => setConfig({ ...config, periods_per_day: e.target.value === '' ? '' : parseInt(e.target.value) })}
                    min="1"
                    max="15"
                    className="max-w-xs"
                    data-testid="periods-per-day-input"
                  />
                </div>
                <div>
                  <Label className="text-base font-medium">Exclude Roles from Auto-Assignment</Label>
                  <p className="text-sm text-slate-500 mb-2">Select roles that should NOT be assigned relief duties automatically</p>
                  <div className="flex flex-wrap gap-3 mt-2">
                    {['hod', 'exco'].map(role => {
                      const excluded = (config.exclude_roles_from_relief || []).includes(role);
                      return (
                        <label key={role} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${excluded ? 'bg-red-50 border-red-200 text-red-800' : 'bg-white border-slate-200 text-slate-600'}`}>
                          <input
                            type="checkbox"
                            checked={excluded}
                            onChange={(e) => {
                              const current = config.exclude_roles_from_relief || [];
                              setConfig({
                                ...config,
                                exclude_roles_from_relief: e.target.checked
                                  ? [...current, role]
                                  : current.filter(r => r !== role)
                              });
                            }}
                            className="rounded"
                            data-testid={`exclude-role-${role}`}
                          />
                          <span className="text-sm font-medium uppercase">{role}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label className="text-base font-medium flex items-center gap-2">
                    <Globe className="h-4 w-4 text-slate-500" />
                    School Timezone
                  </Label>
                  <p className="text-sm text-slate-500 mb-2">All date/time calculations (today's absences, weekly reports, briefing schedule) use this timezone</p>
                  <Select
                    value={config.school_timezone || 'Asia/Singapore'}
                    onValueChange={(v) => setConfig({ ...config, school_timezone: v })}
                  >
                    <SelectTrigger className="max-w-xs" data-testid="school-timezone-select">
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONE_OPTIONS.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
            </div>
          </div>
        </div>
        </Card>
          </div>
        </section>

        {/* ─── Section: Notifications ─── */}
        <section data-testid="section-notifications" id="section-notifications" className="scroll-mt-24">
          <SectionHeader icon={Bell} title="Notifications" description="Email, Telegram, SMS, WhatsApp, and WeChat channels" />
          <div className="space-y-6">
        {/* Notification Channels */}
        <Card className="dashboard-card p-6">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-md bg-green-50">
              <Bell className="h-6 w-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Notification Channels</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="email-notif" className="text-base font-medium">Email Notifications</Label>
                    <p className="text-sm text-slate-500">Send notifications via email</p>
                  </div>
                  <Switch
                    id="email-notif"
                    checked={config.notification_channels.email}
                    onCheckedChange={(checked) => 
                      setConfig({ 
                        ...config, 
                        notification_channels: { ...config.notification_channels, email: checked }
                      })
                    }
                    data-testid="email-notif-toggle"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="telegram-notif" className="text-base font-medium">Telegram Notifications</Label>
                    <p className="text-sm text-slate-500">Send notifications via Telegram</p>
                  </div>
                  <Switch
                    id="telegram-notif"
                    checked={config.notification_channels.telegram}
                    onCheckedChange={(checked) => 
                      setConfig({ 
                        ...config, 
                        notification_channels: { ...config.notification_channels, telegram: checked }
                      })
                    }
                    data-testid="telegram-notif-toggle"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="whatsapp-notif" className="text-base font-medium">WhatsApp Notifications</Label>
                    <p className="text-sm text-slate-500">Send notifications via WhatsApp</p>
                  </div>
                  <Switch
                    id="whatsapp-notif"
                    checked={config.notification_channels.whatsapp}
                    onCheckedChange={(checked) => 
                      setConfig({ 
                        ...config, 
                        notification_channels: { ...config.notification_channels, whatsapp: checked }
                      })
                    }
                    data-testid="whatsapp-notif-toggle"
                  />
                </div>
              </div>
            </div>
          </div>
        </Card>
          </div>
        </section>

        {/* ─── Section: Scheduling ─── */}
        <section data-testid="section-scheduling" id="section-scheduling" className="scroll-mt-24">
          <SectionHeader icon={CalendarIcon} title="Scheduling" description="Disabled dates, class absences, and automated briefings" />
          <div className="space-y-6">
        {/* Disabled Dates */}
        <Card className="dashboard-card p-6">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-md bg-slate-100">
              <CalendarIcon className="h-6 w-6 text-slate-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Disabled Dates</h3>
              <p className="text-sm text-slate-500 mb-4">Dates when the system will be automatically disabled</p>
              
              <div className="flex gap-2 mb-4">
                <Input
                  type="date"
                  value={newDisabledDate}
                  onChange={(e) => setNewDisabledDate(e.target.value)}
                  className="max-w-xs"
                  data-testid="new-disabled-date-input"
                />
                <Button
                  onClick={handleAddDisabledDate}
                  variant="outline"
                  data-testid="add-disabled-date-button"
                >
                  Add Date
                </Button>
              </div>

              {config.disabled_dates && config.disabled_dates.length > 0 ? (
                <div className="space-y-2">
                  {config.disabled_dates.sort().map((date) => (
                    <div
                      key={date}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-md"
                    >
                      <span className="text-sm font-medium text-slate-700">{date}</span>
                      <Button
                        onClick={() => handleRemoveDisabledDate(date)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="remove-disabled-date-button"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No disabled dates configured</p>
              )}
            </div>
          </div>
        </Card>

        {/* Class Absence Declarations */}
        <Card className="dashboard-card p-6" data-testid="class-absences-card">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-md bg-emerald-50">
              <GraduationCap className="h-6 w-6 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900 mb-1">Class Absence Declarations</h3>
              <p className="text-sm text-slate-500 mb-4">Declare classes not following their curriculum timetable during specific date ranges. The auto-assignment engine will skip these classes.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <div>
                  <Label className="text-xs text-slate-500">Class Name</Label>
                  <Input
                    value={classAbsenceForm.class_name}
                    onChange={(e) => setClassAbsenceForm({ ...classAbsenceForm, class_name: e.target.value })}
                    placeholder="e.g., 3A"
                    className="mt-1"
                    data-testid="class-absence-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Start Date</Label>
                  <Input
                    type="date"
                    value={classAbsenceForm.start_date}
                    onChange={(e) => setClassAbsenceForm({ ...classAbsenceForm, start_date: e.target.value })}
                    className="mt-1"
                    data-testid="class-absence-start-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">End Date</Label>
                  <Input
                    type="date"
                    value={classAbsenceForm.end_date}
                    onChange={(e) => setClassAbsenceForm({ ...classAbsenceForm, end_date: e.target.value })}
                    className="mt-1"
                    data-testid="class-absence-end-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Reason (optional)</Label>
                  <Input
                    value={classAbsenceForm.reason}
                    onChange={(e) => setClassAbsenceForm({ ...classAbsenceForm, reason: e.target.value })}
                    placeholder="e.g., Field trip"
                    className="mt-1"
                    data-testid="class-absence-reason-input"
                  />
                </div>
              </div>
              <Button
                onClick={handleAddClassAbsence}
                variant="outline"
                className="mb-4"
                data-testid="add-class-absence-button"
              >
                Declare Class Absence
              </Button>

              {classAbsences.length > 0 ? (
                <div className="space-y-2">
                  {classAbsences.map((ca) => (
                    <div
                      key={ca.id}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-md"
                      data-testid="class-absence-item"
                    >
                      <div className="text-sm">
                        <span className="font-semibold text-slate-900">{ca.class_name}</span>
                        <span className="text-slate-500 mx-2">{ca.start_date} to {ca.end_date}</span>
                        {ca.reason && <span className="text-slate-400">({ca.reason})</span>}
                      </div>
                      <Button
                        onClick={() => handleRemoveClassAbsence(ca.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="remove-class-absence-button"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No class absence declarations active.</p>
              )}
            </div>
          </div>
        </Card>

        {/* Daily Briefing */}
        <Card className="dashboard-card p-6" data-testid="daily-briefing-card">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-md bg-blue-50">
              <Mail className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900 mb-1">Daily Briefing Email</h3>
              <p className="text-sm text-slate-500 mb-5">Send personalized daily briefing emails to all teachers with their schedule, relief duties, and invigilation assignments for the day.</p>

              <div className="space-y-4">
                {/* Automated Schedule */}
                <div className="rounded-md border border-slate-200 p-4" data-testid="briefing-schedule-section">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className="h-4 w-4 text-blue-900" />
                    <p className="text-sm font-medium text-slate-900">Automated Daily Schedule</p>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={config?.daily_briefing_enabled ?? true}
                        onCheckedChange={(checked) => setConfig({ ...config, daily_briefing_enabled: checked })}
                        data-testid="briefing-enabled-switch"
                      />
                      <span className="text-sm text-slate-700">{config?.daily_briefing_enabled !== false ? 'Enabled' : 'Disabled'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-slate-600 whitespace-nowrap">Send at</Label>
                      <Select
                        value={String(config?.daily_briefing_hour ?? 7)}
                        onValueChange={(v) => setConfig({ ...config, daily_briefing_hour: parseInt(v) })}
                      >
                        <SelectTrigger className="w-20" data-testid="briefing-hour-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 24 }, (_, i) => (
                            <SelectItem key={i} value={String(i)}>{String(i).padStart(2, '0')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-slate-400 font-bold">:</span>
                      <Select
                        value={String(config?.daily_briefing_minute ?? 0)}
                        onValueChange={(v) => setConfig({ ...config, daily_briefing_minute: parseInt(v) })}
                      >
                        <SelectTrigger className="w-20" data-testid="briefing-minute-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[0, 15, 30, 45].map(m => (
                            <SelectItem key={m} value={String(m)}>{String(m).padStart(2, '0')}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-xs text-slate-400 ml-1">daily</span>
                    </div>
                  </div>
                  {briefingScheduleInfo && briefingScheduleInfo.next_run_local && (
                    <div className="mt-3 inline-flex items-center gap-2 rounded-md bg-emerald-50 border border-emerald-200 px-3 py-1.5 text-xs text-emerald-800" data-testid="briefing-next-run-badge">
                      <Clock className="h-3 w-3" />
                      <span>
                        Next run: <strong>{new Date(briefingScheduleInfo.next_run_local).toLocaleString('en-SG', { timeZone: briefingScheduleInfo.timezone, weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false })}</strong>
                        {' '}({briefingScheduleInfo.timezone})
                        {Number.isFinite(briefingScheduleInfo.next_run_in_seconds) && briefingScheduleInfo.next_run_in_seconds > 0 && (
                          <span className="text-emerald-700"> · in {(() => {
                            const s = briefingScheduleInfo.next_run_in_seconds;
                            const h = Math.floor(s / 3600);
                            const m = Math.floor((s % 3600) / 60);
                            return h > 0 ? `${h} h ${m} m` : `${m} m`;
                          })()}</span>
                        )}
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-slate-400 mt-3">Briefings are sent automatically at the scheduled time (school timezone) to all active teachers. Changes take effect after saving configuration.</p>
                </div>

                {/* Date picker for manual send */}
                <div>
                  <Label htmlFor="briefing-date" className="text-sm font-medium">Briefing Date</Label>
                  <Input
                    id="briefing-date"
                    type="date"
                    value={briefingDate}
                    onChange={(e) => setBriefingDate(e.target.value)}
                    className="max-w-xs mt-1.5"
                    data-testid="briefing-date-input"
                  />
                </div>

                {/* Preview Section */}
                <div className="rounded-md border border-slate-200 p-4">
                  <p className="text-sm font-medium text-slate-900 mb-3">Preview Briefing</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Select value={selectedPreviewTeacher} onValueChange={setSelectedPreviewTeacher}>
                      <SelectTrigger className="w-64" data-testid="preview-teacher-select">
                        <SelectValue placeholder="Select a teacher" />
                      </SelectTrigger>
                      <SelectContent>
                        {previewTeachers.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          onClick={handlePreviewBriefing}
                          disabled={!selectedPreviewTeacher}
                          data-testid="preview-briefing-button"
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          Preview
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Briefing Email Preview</DialogTitle>
                        </DialogHeader>
                        <div
                          className="mt-2 border rounded-md overflow-hidden"
                          dangerouslySetInnerHTML={{ __html: previewHtml }}
                          data-testid="briefing-preview-content"
                        />
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>

                {/* Send Button */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                  <Button
                    onClick={handleSendBriefing}
                    disabled={sendingBriefing}
                    className="bg-blue-900 hover:bg-blue-800"
                    data-testid="send-briefing-button"
                  >
                    <Send className="mr-2 h-4 w-4" />
                    {sendingBriefing ? 'Sending...' : 'Send Briefing to All Teachers'}
                  </Button>
                  <p className="text-xs text-slate-400">Sends to all active teachers with email addresses</p>
                </div>

                {/* Result */}
                {briefingResult && (
                  <div className="rounded-md border border-slate-200 p-4" data-testid="briefing-result">
                    <p className="text-sm font-medium text-slate-900 mb-2">{briefingResult.message}</p>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div className="bg-emerald-50 rounded-md p-2">
                        <p className="text-lg font-bold text-emerald-700">{briefingResult.sent}</p>
                        <p className="text-xs text-emerald-600">Sent</p>
                      </div>
                      <div className="bg-red-50 rounded-md p-2">
                        <p className="text-lg font-bold text-red-600">{briefingResult.failed}</p>
                        <p className="text-xs text-red-500">Failed</p>
                      </div>
                      <div className="bg-slate-50 rounded-md p-2">
                        <p className="text-lg font-bold text-slate-600">{briefingResult.skipped}</p>
                        <p className="text-xs text-slate-500">Skipped</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
          </div>
        </section>

        {/* ─── Section: Class Levels + Relief Priorities ─── */}
        <section data-testid="section-levels-container" className="scroll-mt-24">
          <ClassLevelsSection
            API={API}
            token={token}
            classLevels={classLevels}
            refetchLevels={fetchClassLevels}
          />
          <ReliefPrioritySection config={config} setConfig={setConfig} />
        </section>

        <section data-testid="section-exams" id="section-exams" className="scroll-mt-24">
          <SectionHeader icon={GraduationCap} title="Exams" description="Invigilation rules and exam venues" />
          <div className="space-y-6">
        {/* Invigilation Business Rules */}
        {invigRules && (
        <Card className="dashboard-card p-6" data-testid="invigilation-rules-card">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-md bg-blue-50">
              <Settings className="h-6 w-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900 mb-1">Exam Invigilation Business Rules</h3>
              <p className="text-sm text-slate-500 mb-4">Configure rules for auto-assigning exam invigilators. All values are customizable.</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                <div>
                  <Label className="text-xs text-slate-500">Max Invigilation Per Teacher / Week</Label>
                  <Input type="number" min="1" max="20" value={invigRules.max_invigilation_per_teacher_per_week ?? ''} onChange={(e) => setInvigRules({...invigRules, max_invigilation_per_teacher_per_week: e.target.value === '' ? '' : parseInt(e.target.value)})} className="mt-1" data-testid="rule-max-week" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Max Invigilation Per Teacher / Day</Label>
                  <Input type="number" min="1" max="5" value={invigRules.max_invigilation_per_teacher_per_day ?? ''} onChange={(e) => setInvigRules({...invigRules, max_invigilation_per_teacher_per_day: e.target.value === '' ? '' : parseInt(e.target.value)})} className="mt-1" data-testid="rule-max-day" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Default Min Invigilators Per Venue</Label>
                  <Input type="number" min="1" max="10" value={invigRules.default_min_invigilators_per_venue ?? ''} onChange={(e) => setInvigRules({...invigRules, default_min_invigilators_per_venue: e.target.value === '' ? '' : parseInt(e.target.value)})} className="mt-1" data-testid="rule-min-venue" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Standby Ratio (standby:main)</Label>
                  <Input value={invigRules.standby_ratio ?? '1:3'} onChange={(e) => setInvigRules({...invigRules, standby_ratio: e.target.value})} placeholder="1:3" className="mt-1" data-testid="rule-standby-ratio" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Roaming Teachers Per Session</Label>
                  <Input type="number" min="0" max="5" value={invigRules.roaming_per_session ?? ''} onChange={(e) => setInvigRules({...invigRules, roaming_per_session: e.target.value === '' ? '' : parseInt(e.target.value)})} className="mt-1" data-testid="rule-roaming" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Max Consecutive Invigilation Days</Label>
                  <Input type="number" min="1" max="10" value={invigRules.max_consecutive_days ?? ''} onChange={(e) => setInvigRules({...invigRules, max_consecutive_days: e.target.value === '' ? '' : parseInt(e.target.value)})} className="mt-1" data-testid="rule-max-consec" />
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                  <div><Label className="text-sm font-medium">Prioritize Subject Teachers</Label><p className="text-xs text-slate-500">Assign teachers who teach the exam subject first</p></div>
                  <Switch checked={invigRules.subject_teacher_priority ?? true} onCheckedChange={(v) => setInvigRules({...invigRules, subject_teacher_priority: v})} data-testid="rule-subject-priority" />
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                  <div><Label className="text-sm font-medium">No Same-Day Relief + Invigilation</Label><p className="text-xs text-slate-500">Prevent assigning both relief and invigilation to a teacher on the same day</p></div>
                  <Switch checked={invigRules.no_same_day_relief_and_invigilation ?? true} onCheckedChange={(v) => setInvigRules({...invigRules, no_same_day_relief_and_invigilation: v})} data-testid="rule-no-same-day" />
                </div>
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                  <div><Label className="text-sm font-medium">Gender Balance</Label><p className="text-xs text-slate-500">Try to assign mixed-gender invigilators per venue</p></div>
                  <Switch checked={invigRules.gender_balance ?? false} onCheckedChange={(v) => setInvigRules({...invigRules, gender_balance: v})} data-testid="rule-gender" />
                </div>
              </div>

              <div className="mb-4">
                <Label className="text-sm font-medium">Exclude Roles from Invigilation</Label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {['hod', 'exco'].map(role => {
                    const excluded = (invigRules.exclude_roles_from_invigilation || []).includes(role);
                    return (
                      <label key={role} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer ${excluded ? 'bg-red-50 border-red-200 text-red-800' : 'bg-white border-slate-200 text-slate-600'}`}>
                        <input type="checkbox" checked={excluded} onChange={(e) => {
                          const current = invigRules.exclude_roles_from_invigilation || [];
                          setInvigRules({...invigRules, exclude_roles_from_invigilation: e.target.checked ? [...current, role] : current.filter(r => r !== role)});
                        }} className="rounded" data-testid={`rule-exclude-${role}`} />
                        <span className="text-sm font-medium uppercase">{role}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Exclude by Job Title */}
              <div className="mb-4">
                <Label className="text-sm font-medium">Exclude Job Titles from Invigilation</Label>
                <p className="text-xs text-slate-500 mb-2">Block entire job titles (e.g., Principal, VP) from ever receiving invigilation duties.</p>
                <div className="flex flex-wrap gap-2">
                  {allJobTitles.map(jt => {
                    const excluded = (invigRules.excluded_job_title_ids || []).includes(jt.id);
                    return (
                      <label key={jt.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-md border cursor-pointer text-xs ${excluded ? 'bg-red-50 border-red-200 text-red-800' : 'bg-white border-slate-200 text-slate-600'}`}>
                        <input type="checkbox" checked={excluded} onChange={(e) => {
                          const current = invigRules.excluded_job_title_ids || [];
                          setInvigRules({ ...invigRules, excluded_job_title_ids: e.target.checked ? [...current, jt.id] : current.filter(x => x !== jt.id) });
                        }} className="rounded" data-testid={`rule-exclude-jobtitle-${jt.id}`} />
                        <span className="font-medium">{jt.title}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Exclude Individual Teachers */}
              <div className="mb-4">
                <Label className="text-sm font-medium">Blocklist — Individual Teachers</Label>
                <p className="text-xs text-slate-500 mb-2">Permanently block specific teachers from invigilation duty (e.g., reserved for administrative roles).</p>
                <div className="flex gap-2 mb-2">
                  <select className="flex-1 h-9 px-2 border border-slate-200 rounded-md text-sm bg-white" data-testid="excluded-user-select" onChange={(e) => {
                    const uid = e.target.value;
                    if (!uid) return;
                    const current = invigRules.excluded_user_ids || [];
                    if (!current.includes(uid)) {
                      setInvigRules({ ...invigRules, excluded_user_ids: [...current, uid] });
                    }
                    e.target.value = '';
                  }}>
                    <option value="">+ Add teacher to blocklist</option>
                    {allTeachers.filter(t => !(invigRules.excluded_user_ids || []).includes(t.id)).map(t => (
                      <option key={t.id} value={t.id}>{t.name} ({t.role})</option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(invigRules.excluded_user_ids || []).map(uid => {
                    const t = allTeachers.find(x => x.id === uid);
                    return (
                      <span key={uid} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-50 border border-red-200 text-red-800 rounded-full text-xs font-medium" data-testid={`excluded-user-chip-${uid}`}>
                        {t?.name || uid}
                        <button type="button" onClick={() => setInvigRules({ ...invigRules, excluded_user_ids: (invigRules.excluded_user_ids || []).filter(x => x !== uid) })} className="hover:text-red-900">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Enhancement Rules */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <Label className="text-xs text-slate-500">Max Sessions Per Exam Season</Label>
                  <Input type="number" min="0" max="50" value={invigRules.max_sessions_per_season ?? 0} onChange={(e) => setInvigRules({ ...invigRules, max_sessions_per_season: e.target.value === '' ? 0 : parseInt(e.target.value) })} className="mt-1" data-testid="rule-max-season" />
                  <p className="text-[10px] text-slate-400 mt-1">0 = unlimited. Balances workload across a ~30-day season window.</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Students Per Invigilator (Capacity)</Label>
                  <Input type="number" min="10" max="60" value={invigRules.students_per_invigilator ?? 30} onChange={(e) => setInvigRules({ ...invigRules, students_per_invigilator: e.target.value === '' ? 30 : parseInt(e.target.value) })} className="mt-1" data-testid="rule-students-ratio" />
                  <p className="text-[10px] text-slate-400 mt-1">Auto-increases main invigilator count when exam has more students than (ratio × venue config).</p>
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-md mb-4">
                <div><Label className="text-sm font-medium">Exclude Same-Subject Teacher (Integrity)</Label><p className="text-xs text-slate-500">Block teachers who teach the exam's subject from invigilating that paper.</p></div>
                <Switch checked={invigRules.exclude_same_subject_teacher ?? false} onCheckedChange={(v) => setInvigRules({ ...invigRules, exclude_same_subject_teacher: v })} data-testid="rule-exclude-subject" />
              </div>

              {/* Custom Rules */}
              <div className="mb-4">
                <Label className="text-sm font-medium">Custom Business Rules / Guidelines</Label>
                <p className="text-xs text-slate-500 mb-2">Add custom rules that are displayed as reminders during assignment</p>
                <div className="flex gap-2 mb-2">
                  <Input value={newCustomRule} onChange={(e) => setNewCustomRule(e.target.value)} placeholder="e.g., Senior teachers should not invigilate PE exams" className="flex-1" data-testid="custom-rule-input" />
                  <Button variant="outline" onClick={() => {
                    if (!newCustomRule.trim()) return;
                    setInvigRules({...invigRules, custom_rules: [...(invigRules.custom_rules || []), newCustomRule.trim()]});
                    setNewCustomRule('');
                  }} data-testid="add-custom-rule">Add</Button>
                </div>
                {(invigRules.custom_rules || []).map((rule, i) => (
                  <div key={i} className="flex items-center justify-between p-2 bg-slate-50 rounded mb-1">
                    <span className="text-sm text-slate-700">{rule}</span>
                    <Button variant="ghost" size="sm" className="text-red-600 h-7" onClick={() => {
                      setInvigRules({...invigRules, custom_rules: invigRules.custom_rules.filter((_, idx) => idx !== i)});
                    }}>Remove</Button>
                  </div>
                ))}
              </div>

              <Button onClick={saveInvigRules} className="bg-blue-900 hover:bg-blue-800" data-testid="save-invig-rules"><Save className="mr-2 h-4 w-4" />Save Business Rules</Button>
            </div>
          </div>
        </Card>
        )}

        {/* Exam Timetable Freeze */}
        <Card className="dashboard-card p-6" data-testid="exam-freeze-card">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-md bg-sky-50">
              <Snowflake className="h-6 w-6 text-sky-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900 mb-1">Freeze Timetable During Exams</h3>
              <p className="text-sm text-slate-500 mb-4">
                Suspend class timetables so every affected teacher is <strong>released from class duty</strong> and becomes eligible for exam invigilation.
                Choose <em>Whole School</em> to freeze the entire timetable, or <em>Specific Classes</em> to release only teachers of those classes.
              </p>

              {/* Scope selector */}
              <div className="flex flex-wrap gap-2 mb-4" data-testid="freeze-scope-selector">
                {[
                  { value: 'whole', label: 'Whole School', desc: 'All classes suspended' },
                  { value: 'level', label: 'By Level', desc: 'E.g., Upper Secondary' },
                  { value: 'class', label: 'Specific Class(es)', desc: 'Selected classes only' },
                ].map(opt => {
                  const active = freezeForm.scope === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFreezeForm({ ...freezeForm, scope: opt.value })}
                      className={`flex-1 sm:flex-none text-left px-4 py-2.5 rounded-md border transition-colors ${active ? 'bg-sky-50 border-sky-400 ring-1 ring-sky-300' : 'bg-white border-slate-200 hover:border-slate-300'}`}
                      data-testid={`freeze-scope-${opt.value}`}
                    >
                      <div className={`text-sm font-semibold ${active ? 'text-sky-900' : 'text-slate-800'}`}>{opt.label}</div>
                      <div className="text-[11px] text-slate-500">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>

              <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4`}>
                {freezeForm.scope === 'level' && (
                  <div className="col-span-full">
                    <Label className="text-xs text-slate-500">Level</Label>
                    {classLevels.length === 0 ? (
                      <p className="mt-1 text-sm text-slate-500 italic">
                        No class levels configured. Add some under <strong>Class Levels</strong> above first.
                      </p>
                    ) : (
                      <select
                        value={freezeForm.level_id}
                        onChange={(e) => setFreezeForm({ ...freezeForm, level_id: e.target.value })}
                        className="mt-1 w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm"
                        data-testid="freeze-level-select"
                      >
                        <option value="">Select level…</option>
                        {classLevels.map(l => (
                          <option key={l.id} value={l.id}>{l.name} ({(l.classes || []).length} classes)</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}
                {freezeForm.scope === 'class' && (
                  <div className="relative col-span-full">
                    <Label className="text-xs text-slate-500">Classes</Label>
                    <div
                      className="mt-1 min-h-[42px] flex flex-wrap items-center gap-1.5 px-2 py-1.5 border border-slate-200 rounded-md bg-white focus-within:border-sky-400 focus-within:ring-1 focus-within:ring-sky-300"
                      onClick={(e) => {
                        const input = e.currentTarget.querySelector('input');
                        if (input) input.focus();
                      }}
                      data-testid="freeze-classes-chip-input"
                    >
                      {freezeForm.classes.map(cls => (
                        <span
                          key={cls}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 text-xs font-medium"
                          data-testid={`freeze-class-chip-${cls}`}
                        >
                          {cls}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeClassChip(cls); }}
                            className="hover:text-sky-900"
                            aria-label={`Remove ${cls}`}
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        value={classInput}
                        onChange={(e) => { setClassInput(e.target.value); setShowClassSuggestions(true); }}
                        onFocus={() => setShowClassSuggestions(true)}
                        onBlur={() => setTimeout(() => setShowClassSuggestions(false), 150)}
                        onKeyDown={handleClassInputKey}
                        placeholder={freezeForm.classes.length === 0 ? 'Type to search classes, press Enter to add' : 'Add another...'}
                        className="flex-1 min-w-[160px] text-sm outline-none bg-transparent py-0.5"
                        data-testid="freeze-classes-input"
                      />
                    </div>
                    {showClassSuggestions && classSuggestions.length > 0 && (
                      <div
                        className="absolute z-30 left-0 right-0 mt-1 max-h-56 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg"
                        data-testid="freeze-class-suggestions"
                      >
                        {classSuggestions.map(c => (
                          <button
                            key={c}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => addClassChip(c)}
                            className="w-full text-left px-3 py-1.5 text-sm hover:bg-sky-50 hover:text-sky-900 transition-colors"
                            data-testid={`freeze-class-suggestion-${c}`}
                          >
                            {c}
                          </button>
                        ))}
                      </div>
                    )}
                    <p className="mt-1 text-[11px] text-slate-400">Press <kbd className="px-1 rounded bg-slate-100">Enter</kbd> or <kbd className="px-1 rounded bg-slate-100">,</kbd> to add. Suggestions are drawn from your timetables & exams.</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-slate-500">Start Date</Label>
                  <Input
                    type="date"
                    value={freezeForm.start_date}
                    onChange={(e) => setFreezeForm({ ...freezeForm, start_date: e.target.value })}
                    className="mt-1"
                    data-testid="freeze-start-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">End Date</Label>
                  <Input
                    type="date"
                    value={freezeForm.end_date}
                    onChange={(e) => setFreezeForm({ ...freezeForm, end_date: e.target.value })}
                    className="mt-1"
                    data-testid="freeze-end-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Reason (optional)</Label>
                  <Input
                    value={freezeForm.reason}
                    onChange={(e) => setFreezeForm({ ...freezeForm, reason: e.target.value })}
                    placeholder="e.g., End-of-Year Exam Week"
                    className="mt-1"
                    data-testid="freeze-reason-input"
                  />
                </div>
              </div>
              <Button
                onClick={handleAddFreezePeriod}
                variant="outline"
                className="mb-4 border-sky-300 text-sky-700 hover:bg-sky-50"
                data-testid="add-freeze-button"
              >
                <Snowflake className="mr-2 h-4 w-4" />
                Add Freeze Period
              </Button>

              {/* Unified list — whole-school freeze + class-scoped suspensions */}
              {(freezePeriods.length > 0 || classAbsences.length > 0) ? (
                <div className="space-y-2" data-testid="freeze-period-list">
                  {freezePeriods.map((fp) => {
                    const isLevel = fp.scope === 'level';
                    return (
                    <div
                      key={`fp-${fp.id}`}
                      className={`flex items-center justify-between p-3 ${isLevel ? 'bg-indigo-50/60 border-indigo-100' : 'bg-sky-50/60 border-sky-100'} border rounded-md`}
                      data-testid="freeze-period-item"
                    >
                      <div className="flex items-center gap-2 text-sm flex-wrap">
                        <span className={`px-1.5 py-0.5 rounded ${isLevel ? 'bg-indigo-600' : 'bg-sky-600'} text-white text-[10px] font-semibold uppercase tracking-wide`}>
                          {isLevel ? (fp.level_name || 'Level') : 'Whole School'}
                        </span>
                        <span className="font-semibold text-slate-900">{fp.start_date} → {fp.end_date}</span>
                        {isLevel && fp.classes?.length > 0 && (
                          <span className="text-[11px] text-slate-500">{fp.classes.length} class{fp.classes.length === 1 ? '' : 'es'}</span>
                        )}
                        {fp.reason && <span className="text-slate-500">({fp.reason})</span>}
                        {fp.created_by && <span className="text-[11px] text-slate-400">by {fp.created_by}</span>}
                      </div>
                      <Button
                        onClick={() => handleRemoveFreezePeriod(fp.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="remove-freeze-button"
                      >
                        Remove
                      </Button>
                    </div>
                    );
                  })}
                  {classAbsences.map((ca) => (
                    <div
                      key={`ca-${ca.id}`}
                      className="flex items-center justify-between p-3 bg-emerald-50/60 border border-emerald-100 rounded-md"
                      data-testid="freeze-class-item"
                    >
                      <div className="flex items-center gap-2 text-sm flex-wrap">
                        <span className="px-1.5 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-semibold uppercase tracking-wide">Class</span>
                        <span className="font-semibold text-slate-900">{ca.class_name}</span>
                        <span className="text-slate-500">{ca.start_date} → {ca.end_date}</span>
                        {ca.reason && <span className="text-slate-400">({ca.reason})</span>}
                      </div>
                      <Button
                        onClick={() => handleRemoveClassAbsence(ca.id)}
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="remove-class-freeze-button"
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No freeze periods configured. Smart Assign will use regular timetable checks.</p>
              )}
            </div>
          </div>
        </Card>

        {/* Venue Management */}
        <Card className="dashboard-card p-6" data-testid="venues-card">
          <div className="flex items-start space-x-4">
            <div className="p-3 rounded-md bg-indigo-50">
              <CalendarIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-slate-900 mb-1">Exam Venues</h3>
              <p className="text-sm text-slate-500 mb-4">Configure venues with capacity and required number of invigilators. The auto-assign engine uses this to determine how many invigilators to assign per venue.</p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                <div>
                  <Label className="text-xs text-slate-500">Venue Name</Label>
                  <Input value={newVenue.name} onChange={(e) => setNewVenue({...newVenue, name: e.target.value})} placeholder="e.g., Hall A" className="mt-1" data-testid="venue-name-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Capacity (students)</Label>
                  <Input type="number" min="1" value={newVenue.capacity ?? ''} onChange={(e) => setNewVenue({...newVenue, capacity: e.target.value === '' ? '' : parseInt(e.target.value)})} className="mt-1" data-testid="venue-capacity-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Min Invigilators</Label>
                  <Input type="number" min="1" max="20" value={newVenue.min_invigilators ?? ''} onChange={(e) => setNewVenue({...newVenue, min_invigilators: e.target.value === '' ? '' : parseInt(e.target.value)})} className="mt-1" data-testid="venue-invig-input" />
                </div>
              </div>
              <Button onClick={handleAddVenue} variant="outline" className="mb-4" data-testid="add-venue-button">Add / Update Venue</Button>

              {venues.length > 0 ? (
                <div className="space-y-2">
                  {venues.map((v) => (
                    <div key={v.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-md" data-testid="venue-item">
                      <div className="text-sm">
                        <span className="font-semibold text-slate-900">{v.name}</span>
                        <span className="text-slate-500 mx-2">Capacity: {v.capacity}</span>
                        <span className="text-slate-500">Invigilators: {v.min_invigilators}</span>
                      </div>
                      <Button onClick={() => handleDeleteVenue(v.id)} variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="remove-venue-button">Remove</Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No venues configured. Default invigilator count from business rules will be used.</p>
              )}
            </div>
          </div>
        </Card>
          </div>
        </section>
      </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button
            onClick={handleSaveConfig}
            disabled={saving}
            className="bg-blue-900 hover:bg-blue-800"
            data-testid="save-config-button"
          >
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
    </div>
  );
};

export default ConfigurationPage;

// ── Keyword search for config cards ──
// Scans all Cards on the page, matches query against their visible text,
// scrolls to the first match and briefly highlights it.
const SEARCH_INDEX = [
  { testid: 'exam-freeze-card', label: 'Freeze Timetable During Exams', keywords: 'freeze timetable exam invigilation release class whole school suspend' },
  { testid: 'venues-card', label: 'Venue Management', keywords: 'venue room capacity invigilator hall' },
  { testid: 'invigilation-rules-card', label: 'Invigilation Business Rules', keywords: 'invigilation rules ratio standby roaming exclude special roles' },
  { testid: 'class-absences-card', label: 'Class Absences', keywords: 'class absence field trip suspended' },
  { testid: 'daily-briefing-card', label: 'Daily Briefing', keywords: 'daily briefing morning email summary' },
  // Section anchors — jump to an entire section
  { testid: 'section-general', label: 'General & System', keywords: 'general system timezone locale school information periods workload' },
  { testid: 'section-notifications', label: 'Notifications', keywords: 'notifications email telegram whatsapp resend channel ai schoolzest' },
  { testid: 'section-scheduling', label: 'Scheduling', keywords: 'scheduling holidays non-teaching weekend term' },
  { testid: 'section-exams', label: 'Exams', keywords: 'exam invigilation freeze venue rules' },
];

const ConfigSearch = () => {
  const [q, setQ] = React.useState('');
  const [open, setOpen] = React.useState(false);

  const results = React.useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return [];
    // Index-based primary match
    const indexHits = SEARCH_INDEX.filter(entry =>
      entry.label.toLowerCase().includes(query) || entry.keywords.toLowerCase().includes(query)
    );
    // DOM fallback — match any card by visible text
    const domHits = [];
    const seen = new Set(indexHits.map(h => h.testid));
    document.querySelectorAll('[data-testid$="-card"], [data-testid^="section-"]').forEach(el => {
      const testid = el.getAttribute('data-testid');
      // Exclude search-result buttons + nested elements
      if (!testid || testid.startsWith('config-search-result-')) return;
      if (seen.has(testid)) return;
      // Only consider elements that ARE a section/card, not anything inside the sticky nav bar
      if (el.closest('[data-testid="config-search-results"]')) return;
      const txt = (el.textContent || '').toLowerCase();
      if (txt.includes(query)) {
        const heading = el.querySelector('h3, h2')?.textContent?.trim() || testid;
        domHits.push({ testid, label: heading, keywords: '' });
        seen.add(testid);
      }
    });
    return [...indexHits, ...domHits].slice(0, 6);
  }, [q]);

  const jumpTo = (testid) => {
    const el = document.querySelector(`[data-testid="${testid}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Pulse ring highlight
      el.classList.add('ring-2', 'ring-blue-500', 'ring-offset-2', 'transition-all');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-blue-500', 'ring-offset-2', 'transition-all');
      }, 2200);
    }
    setOpen(false);
    setQ('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && results.length > 0) {
      e.preventDefault();
      jumpTo(results[0].testid);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative ml-auto w-full sm:w-72" data-testid="config-search-wrapper">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
          placeholder="Search settings (e.g., freeze, venue, AI)..."
          className="w-full pl-8 pr-3 py-1.5 text-xs border border-slate-200 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          data-testid="config-search-input"
        />
      </div>
      {open && q.trim() && (
        <div
          className="absolute z-30 top-full left-0 right-0 mt-1 max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg"
          data-testid="config-search-results"
        >
          {results.length > 0 ? (
            results.map(r => (
              <button
                key={r.testid}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => jumpTo(r.testid)}
                className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 hover:text-blue-900 border-b border-slate-100 last:border-0"
                data-testid={`config-search-result-${r.testid}`}
              >
                <div className="font-medium">{r.label}</div>
                {r.keywords && <div className="text-[10px] text-slate-400 mt-0.5">{r.keywords.split(' ').slice(0, 5).join(' · ')}</div>}
              </button>
            ))
          ) : (
            <div className="px-3 py-2 text-xs text-slate-500">No matching settings. Try keywords like <em>freeze</em>, <em>venue</em>, <em>notifications</em>.</div>
          )}
        </div>
      )}
    </div>
  );
};
